<template>
  <div class="activityPageEdit">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>内容</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ name: 'activityPageList' }">自定义活动页</el-breadcrumb-item>
      <el-breadcrumb-item>{{ uuid ? '编辑活动页' : '新建活动页' }}</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="builder" v-loading="loading">
      <!-- 左：组件库 + 已加组件 -->
      <div class="pane pane-left">
        <div class="pane-title">组件库</div>
        <!--
          目前只有"图片广告"一种组件、"单行一海报"一种排列方式，所以这里是一个固定
          按钮而不是"拖拽到画布"。仓里 9 处 draggable 全是扁平列表内重排、没有跨容器
          drop，做拖入画布等于从零搭一个装修器 —— 等真出现第二种组件再说。
          后端的 activity_page_component 表已经留好了这一层，加组件不用改表。
        -->
        <el-button class="add-component" icon="el-icon-plus" @click="addComponent">图片广告</el-button>

        <div class="pane-title mg-t-20">页面结构</div>
        <p v-if="!components.length" class="font-12 color-999">还没有组件，点上面的「图片广告」加一个。</p>
        <draggable v-model="components" handle=".drag-handle">
          <div
            v-for="(component, index) in components"
            :key="component._k"
            class="component-item"
            :class="{ on: activeIndex === index }"
            @click="selectComponent(index)"
          >
            <i class="el-icon-rank drag-handle"></i>
            <span>图片广告（{{ component.images.length }}张）</span>
            <i class="el-icon-delete" @click.stop="removeComponent(index)"></i>
          </div>
        </draggable>
      </div>

      <!-- 中：手机预览。就是把图按 750 宽等比铺下来，所见即所得 -->
      <div class="pane pane-center">
        <div class="phone">
          <div class="phone-bar">{{ pageForm.title || '页面标题' }}</div>
          <div class="phone-body">
            <template v-for="(component, ci) in components">
              <div
                v-for="image in component.images"
                :key="image._k"
                class="preview-img"
                :class="{ on: activeIndex === ci }"
                @click="selectComponent(ci)"
              >
                <img :src="image.image" />
              </div>
            </template>
            <p v-if="!totalImages" class="empty">预览区：右侧加了图片就会显示在这里</p>
          </div>
        </div>
      </div>

      <!-- 右：页面设置 / 组件设置 -->
      <div class="pane pane-right">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="页面设置" name="page">
            <el-form :model="pageForm" label-width="100px" size="small">
              <el-form-item label="页面标题" required>
                <el-input v-model="pageForm.title" maxlength="100" show-word-limit placeholder="也是小程序的标题栏文案"></el-input>
              </el-form-item>
              <el-form-item label="分享标题">
                <el-input v-model="pageForm.share_title" maxlength="100" placeholder="留空则与页面标题一致"></el-input>
              </el-form-item>
              <el-form-item label="分享描述">
                <el-input v-model="pageForm.share_desc" maxlength="255"></el-input>
              </el-form-item>
              <el-form-item label="分享海报">
                <uploadImageBtn @getImageUrl="url => (pageForm.share_image = url)" />
                <div v-if="pageForm.share_image" class="share-image">
                  <img :src="pageForm.share_image" />
                  <i class="el-icon-delete" @click="pageForm.share_image = ''"></i>
                </div>
                <p class="font-12 color-999">建议 4:3</p>
              </el-form-item>
              <el-form-item label="上架设置" required>
                <el-radio v-model="pageForm.shelf_type" :label="1">立即上架</el-radio>
                <el-radio v-model="pageForm.shelf_type" :label="2">定时上架</el-radio>
                <el-date-picker
                  v-if="pageForm.shelf_type == 2"
                  v-model="shelfRange"
                  type="datetimerange"
                  range-separator="至"
                  start-placeholder="开始时间"
                  end-placeholder="结束时间"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  class="mg-t-10"
                >
                </el-date-picker>
                <p v-if="pageForm.shelf_type == 2" class="font-12 color-999">
                  定时上架是「生效窗口」：窗口外小程序打开就是已下架。没有定时任务去翻状态。
                </p>
              </el-form-item>
              <el-form-item label="排序">
                <el-input-number v-model="pageForm.sort" :min="0" :step="1"></el-input-number>
                <span class="font-12 color-999 mg-l-10">数字小的在前</span>
              </el-form-item>
            </el-form>
          </el-tab-pane>

          <el-tab-pane label="组件设置" name="component">
            <p v-if="!currentComponent" class="font-12 color-999">先在左边选一个组件。</p>
            <template v-else>
              <el-form label-width="100px" size="small">
                <el-form-item label="排列方式">
                  <el-select v-model="currentComponent.layout">
                    <el-option :value="1" label="单行一海报"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="添加图片">
                  <uploadImagesBtn @getImageUrl="onUploadImage" />
                  <el-button type="text" @click="imageDialogVisible = true">从素材库选择</el-button>
                  <p class="font-12 color-999">建议宽 750，拖拽可调整顺序</p>
                </el-form-item>
              </el-form>

              <draggable v-model="currentComponent.images" handle=".drag-handle">
                <!--
                  ⚠ key 用的是每行自己的 _k，不是下标。用下标的话拖动排序或删掉中间
                    一张图之后，Vue 会按下标就地复用组件实例，富文本编辑器里显示的
                    还是上一行的内容。
                -->
                <div v-for="image in currentComponent.images" :key="image._k" class="image-row">
                  <div class="image-row-head">
                    <i class="el-icon-rank drag-handle"></i>
                    <img :src="image.image" class="thumb" />
                    <span class="flex-1 font-12 color-999">
                      {{ image.url_type ? '已配跳转' : '不跳转' }}
                    </span>
                    <i class="el-icon-delete" @click="removeImage(image)"></i>
                  </div>
                  <el-form label-width="100px" size="small" class="mg-t-10">
                    <jumpConfig ref="jumpConfigs" :row="image" :self-page-uuid="uuid" />
                  </el-form>
                </div>
              </draggable>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <div class="operateBtn">
      <el-button size="medium" type="primary" :loading="saving" @click="submit">保存</el-button>
      <el-button size="medium" @click="$router.go(-1)">返回</el-button>
    </div>

    <el-dialog title="选择图片" :visible.sync="imageDialogVisible" width="80%" :close-on-click-modal="false">
      <imageDialog @closeImageDialog="imageDialogVisible = false" @confirmImageDialog="onPickImages" />
    </el-dialog>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'
  import imageDialog from '../../../components/imageDialog.vue'
  import uploadImageBtn from '../../../components/uploadImageBtn.vue'
  import uploadImagesBtn from '../../../components/uploadImagesBtn.vue'
  import jumpConfig from '../../../components/jumpConfig.vue'
  import { emptyRow } from '../../../components/jumpRow'

  export default {
    name: 'activityPageEdit',
    components: { draggable, imageDialog, uploadImageBtn, uploadImagesBtn, jumpConfig },
    data() {
      return {
        uuid: '',
        loading: false,
        saving: false,
        activeTab: 'page',
        activeIndex: -1,
        imageDialogVisible: false,
        shelfRange: [],
        pageForm: {
          title: '',
          share_title: '',
          share_desc: '',
          share_image: '',
          shelf_type: 1,
          shelf_start_time: '',
          shelf_end_time: '',
          sort: 0
        },
        components: []
      }
    },
    computed: {
      currentComponent() {
        return this.components[this.activeIndex] || null
      },
      totalImages() {
        return this.components.reduce((sum, c) => sum + c.images.length, 0)
      }
    },
    created() {
      this.uuid = this.$route.query.uuid || ''
      if (this.uuid) this.getDetail()
    },
    methods: {
      getDetail() {
        this.loading = true
        this.$api.activityPage
          .getDetail(this.uuid)
          .then(res => {
            Object.keys(this.pageForm).forEach(key => {
              if (res[key] !== undefined && res[key] !== null) this.pageForm[key] = res[key]
            })
            if (this.pageForm.shelf_type == 2) {
              this.shelfRange = [this.pageForm.shelf_start_time, this.pageForm.shelf_end_time]
            }
            // 把后端返回的图补齐成"所有键都在"的形状 —— Vue 2 对后加的属性不做响应式，
            // 少一个键，那个字段在跳转配置里就是改了不刷新。
            this.components = (res.components || []).map(c => ({
              _k: nextKey(),
              type: c.type,
              layout: c.layout,
              images: (c.images || []).map(img =>
                Object.assign(emptyRow(), { _k: nextKey(), image: img.image }, pickJump(img))
              )
            }))
            if (this.components.length) this.activeIndex = 0
          })
          .catch(err => this.$message.error(err))
          .finally(() => {
            this.loading = false
          })
      },

      /**
       * 收一遍当前挂着的 jumpConfig 的富文本内容。
       * $refs.jumpConfigs 只包含**当前选中组件**下的那几个（右侧面板是 v-else 渲染的），
       * 所以切组件之前必须先收一次 —— 否则在 A 组件写完富文本、点到 B 再保存，
       * A 那张图存回去的还是加载时的旧值，界面上还提示"保存成功"。
       */
      collectEditors() {
        const list = this.$refs.jumpConfigs
        if (!list) return
        ;(Array.isArray(list) ? list : [list]).forEach(e => e.collect())
      },

      // 选中组件时顺手把右侧切到「组件设置」—— 不然点了左边看不出哪里变了
      selectComponent(index) {
        if (index !== this.activeIndex) this.collectEditors()
        this.activeIndex = index
        this.activeTab = 'component'
      },
      addComponent() {
        this.components.push({ _k: nextKey(), type: 1, layout: 1, images: [] })
        this.selectComponent(this.components.length - 1)
      },
      removeComponent(index) {
        this.components.splice(index, 1)
        if (this.activeIndex >= this.components.length) this.activeIndex = this.components.length - 1
      },
      onUploadImage(url) {
        this.pushImage(url)
      },
      onPickImages(list) {
        ;(list || []).forEach(i => this.pushImage(i.url))
        this.imageDialogVisible = false
      },
      pushImage(url) {
        if (!this.currentComponent) return this.$message.warning('先在左边选一个组件')
        this.currentComponent.images.push(Object.assign(emptyRow(), { _k: nextKey(), image: url }))
      },
      removeImage(image) {
        const index = this.currentComponent.images.indexOf(image)
        if (index !== -1) this.currentComponent.images.splice(index, 1)
      },

      submit() {
        // ⚠ 富文本是 ref 取值的，不 collect 就会把运营刚写的内容丢掉存成空。
        this.collectEditors()

        if (!this.pageForm.title) return this.$message.warning('请填写页面标题')
        if (this.pageForm.shelf_type == 2) {
          if (!this.shelfRange || this.shelfRange.length !== 2) return this.$message.warning('请选择定时上架的时间范围')
          this.pageForm.shelf_start_time = this.shelfRange[0]
          this.pageForm.shelf_end_time = this.shelfRange[1]
        } else {
          this.pageForm.shelf_start_time = ''
          this.pageForm.shelf_end_time = ''
        }

        const payload = Object.assign({}, this.pageForm, {
          // sort 由数组顺序决定，跟左侧和图片列表里拖出来的顺序一致
          components: this.components.map((c, ci) => ({
            type: c.type,
            layout: c.layout,
            sort: ci,
            images: c.images.map((img, ii) => Object.assign({}, img, { sort: ii }))
          }))
        })

        this.saving = true
        const request = this.uuid
          ? this.$api.activityPage.update(this.uuid, payload)
          : this.$api.activityPage.create(payload)
        request
          .then(res => {
            this.$message.success('保存成功')
            // 新建完直接留在编辑页并挂上 uuid，否则再点一次保存会建出第二个页面
            if (!this.uuid && res && res.uuid) {
              this.uuid = res.uuid
              this.$router.replace({ name: 'activityPageEdit', query: { uuid: res.uuid } })
            }
          })
          .catch(err => this.$message.error(err))
          .finally(() => {
            this.saving = false
          })
      }
    }
  }

  /**
   * 给组件和图发一个本地唯一 key，只在前端用（v-for 的 :key）。
   * 用下标当 key 的话，拖拽排序或删掉中间一行之后 Vue 会就地复用组件实例，
   * 富文本编辑器里会显示上一行的内容。
   * 后端 saveComponents 走白名单，_k 提交上去会被丢掉。
   */
  let keySeed = 0
  function nextKey() {
    keySeed += 1
    return 'k' + keySeed
  }

  /**
   * 只挑跳转相关的键，别把后端返回的 uuid / create_time / url_type_name 之类
   * 一起塞回 row —— 那些提交回去会被后端白名单挡掉，但留在本地会让"已配跳转"
   * 的判断变糊涂。
   *
   * ⚠ url 特意**不取**后台详情里的派生值：后端 cmsDetail 传的是运营原样填的值
   * （loadComponents 的 derive_url=false），这里取的就是原值。
   */
  function pickJump(img) {
    const keys = Object.keys(emptyRow())
    const out = {}
    keys.forEach(key => {
      if (img[key] !== undefined) out[key] = img[key]
    })
    return out
  }
</script>

<style scoped>
  .builder {
    display: flex;
    align-items: flex-start;
    margin-top: 20px;
  }
  .pane {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    padding: 16px;
  }
  .pane-left {
    width: 220px;
    flex: none;
  }
  .pane-center {
    flex: none;
    width: 415px;
    margin: 0 16px;
  }
  .pane-right {
    flex: 1;
    min-width: 0;
  }
  .pane-title {
    font-weight: 600;
    margin-bottom: 10px;
  }
  .add-component {
    width: 100%;
  }
  .component-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    margin-bottom: 8px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
  }
  .component-item.on {
    border-color: #409eff;
    background: #ecf5ff;
  }
  .component-item span {
    flex: 1;
    margin: 0 8px;
  }
  .drag-handle {
    cursor: move;
    color: #909399;
  }
  .phone {
    width: 375px;
    margin: 0 auto;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    overflow: hidden;
  }
  .phone-bar {
    height: 44px;
    line-height: 44px;
    text-align: center;
    background: #f7f8fa;
    border-bottom: 1px solid #ebeef5;
    font-size: 14px;
  }
  .phone-body {
    min-height: 500px;
    max-height: 640px;
    overflow-y: auto;
    background: #fff;
  }
  .phone-body .empty {
    padding: 40px 20px;
    text-align: center;
    color: #999;
    font-size: 12px;
  }
  .preview-img {
    line-height: 0;
    cursor: pointer;
    border: 2px solid transparent;
  }
  .preview-img.on {
    border-color: #409eff;
  }
  /* 750 设计稿等比铺满 375 宽的预览区 */
  .preview-img img {
    width: 100%;
    display: block;
  }
  .image-row {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 12px;
  }
  .image-row-head {
    display: flex;
    align-items: center;
  }
  .image-row-head .thumb {
    width: 60px;
    height: 60px;
    object-fit: cover;
    margin: 0 10px;
  }
  .image-row-head .flex-1 {
    flex: 1;
  }
  .image-row-head .el-icon-delete {
    cursor: pointer;
    color: #f56c6c;
  }
  .share-image {
    position: relative;
    display: inline-block;
    margin-top: 10px;
  }
  .share-image img {
    width: 160px;
    display: block;
  }
  .share-image .el-icon-delete {
    position: absolute;
    right: 6px;
    top: 6px;
    color: #fff;
    cursor: pointer;
  }
  .operateBtn {
    margin-top: 20px;
    text-align: center;
  }
</style>
